/**
 * Universal Image Helper with Autonomous Client-Side SVG Vector Fallbacks
 * Falls back to a placeholder when an image is missing, so a broken listing
 * photo never leaves an empty frame on the page.
 */

export interface PresetTheme {
  title: string;
  subtitle: string;
  gradientTop: string;
  gradientBottom: string;
  accent: string;
  iconType: 'townhouse' | 'interior' | 'waterfront' | 'penthouse' | 'mansion' | 'kitchen' | 'loft' | 'pool' | 'logo' | 'banner';
}

export const PRESET_DEFINITIONS: Record<string, PresetTheme> = {
  'prop-asset-1': {
    title: 'Modern Luxury Townhome',
    subtitle: 'Leslieville • Toronto',
    gradientTop: '#1e2430',
    gradientBottom: '#0e1117',
    accent: '#c5a880',
    iconType: 'townhouse'
  },
  'prop-asset-2': {
    title: 'Contemporary Open Interior',
    subtitle: 'Sun-Filled Living & Dining',
    gradientTop: '#24201d',
    gradientBottom: '#12100e',
    accent: '#e5d3b3',
    iconType: 'interior'
  },
  'prop-asset-3': {
    title: 'Waterfront Estate & Dock',
    subtitle: 'Bedford Basin • Halifax',
    gradientTop: '#10222f',
    gradientBottom: '#0a141c',
    accent: '#70b5d9',
    iconType: 'waterfront'
  },
  'prop-asset-4': {
    title: 'Architectural Deck & Pool',
    subtitle: 'Harbor Views & Sunset Terrace',
    gradientTop: '#1e1c2b',
    gradientBottom: '#100e18',
    accent: '#b89bd9',
    iconType: 'pool'
  },
  'prop-asset-5': {
    title: 'Historic Stone Residence',
    subtitle: 'Old Quebec • Historic District',
    gradientTop: '#2c251e',
    gradientBottom: '#17130e',
    accent: '#d4af37',
    iconType: 'mansion'
  },
  'prop-asset-6': {
    title: 'Gourmet Chef Kitchen',
    subtitle: 'Marble Island & Custom Wood',
    gradientTop: '#1c2024',
    gradientBottom: '#0f1214',
    accent: '#e2d5c3',
    iconType: 'kitchen'
  },
  'prop-asset-7': {
    title: 'Bow River Penthouse',
    subtitle: 'Downtown Calgary Skyview',
    gradientTop: '#131e2b',
    gradientBottom: '#091019',
    accent: '#82c0e8',
    iconType: 'penthouse'
  },
  'prop-asset-8': {
    title: 'Master Suite & Spa Bath',
    subtitle: 'Floor-to-Ceiling Glass Views',
    gradientTop: '#261e22',
    gradientBottom: '#130d10',
    accent: '#e5b3c3',
    iconType: 'interior'
  },
  'prop-asset-9': {
    title: 'Craftsman Luxury Duplex',
    subtitle: 'Kitsilano • Vancouver',
    gradientTop: '#18241d',
    gradientBottom: '#0c140f',
    accent: '#8fd4a2',
    iconType: 'townhouse'
  },
  'prop-asset-10': {
    title: 'Heritage Brick Loft',
    subtitle: 'Old Montreal • High Ceilings',
    gradientTop: '#2b1f1a',
    gradientBottom: '#160e0a',
    accent: '#e6986e',
    iconType: 'loft'
  },
  'test-residence': {
    title: 'Verified Canadian Residence',
    subtitle: 'Architectural Real Estate',
    gradientTop: '#1b1e23',
    gradientBottom: '#0d0f12',
    accent: '#c5a880',
    iconType: 'mansion'
  },
  'agency-apex-logo': {
    title: 'Real Estate Agency',
    subtitle: 'Real Estate Channel',
    gradientTop: '#111827',
    gradientBottom: '#030712',
    accent: '#c5a880',
    iconType: 'logo'
  },
  'agency-apex-banner': {
    title: 'REAL ESTATE AGENCY',
    subtitle: 'REAL ESTATE CHANNEL',
    gradientTop: '#0d1117',
    gradientBottom: '#161b22',
    accent: '#c5a880',
    iconType: 'banner'
  }
};

function escapeXml(unsafe: string): string {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function buildSvgVectorString(key: string): string {
  const cleanKey = key
    .replace(/^\/?uploads\//, '')
    .replace(/\.(webp|avif|png|jpg|jpeg|svg)$/i, '')
    .trim();

  const theme = PRESET_DEFINITIONS[cleanKey] || {
    title: 'Verified Canadian Property',
    subtitle: 'Real Estate Channel',
    gradientTop: '#1a1d24',
    gradientBottom: '#0e1014',
    accent: '#c5a880',
    iconType: 'townhouse' as const
  };

  const titleEsc = escapeXml(theme.title);
  const subEsc = escapeXml(theme.subtitle.toUpperCase());

  if (theme.iconType === 'logo') {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 600" width="600" height="600">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="${theme.gradientTop}" />
          <stop offset="100%" stop-color="${theme.gradientBottom}" />
        </linearGradient>
        <linearGradient id="gold" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#f3e7cb" />
          <stop offset="50%" stop-color="${theme.accent}" />
          <stop offset="100%" stop-color="#9d7e52" />
        </linearGradient>
      </defs>
      <rect width="600" height="600" fill="url(#bg)" rx="40" />
      <circle cx="300" cy="260" r="140" fill="#ffffff" fill-opacity="0.03" />
      <polygon points="300,160 400,330 200,330" fill="none" stroke="url(#gold)" stroke-width="12" stroke-linejoin="round" />
      <path d="M 240 290 L 360 290 L 300 210 Z" fill="url(#gold)" />
      <text x="300" y="420" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" font-size="40" font-weight="800" fill="#ffffff" text-anchor="middle" letter-spacing="4">${titleEsc}</text>
      <text x="300" y="470" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" font-size="18" font-weight="600" fill="${theme.accent}" text-anchor="middle" letter-spacing="6">${subEsc}</text>
    </svg>`;
  }

  if (theme.iconType === 'banner') {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 600" width="1600" height="600">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#0a0d14" />
          <stop offset="50%" stop-color="#141a24" />
          <stop offset="100%" stop-color="#07090d" />
        </linearGradient>
        <linearGradient id="gold" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stop-color="#e5d3b3" />
          <stop offset="100%" stop-color="#c5a880" />
        </linearGradient>
      </defs>
      <rect width="1600" height="600" fill="url(#bg)" />
      <g opacity="0.15">
        <line x1="0" y1="100" x2="1600" y2="100" stroke="#ffffff" stroke-width="1" stroke-dasharray="8 8" />
        <line x1="0" y1="300" x2="1600" y2="300" stroke="#ffffff" stroke-width="1" stroke-dasharray="8 8" />
        <line x1="0" y1="500" x2="1600" y2="500" stroke="#ffffff" stroke-width="1" stroke-dasharray="8 8" />
      </g>
      <rect x="80" y="80" width="1440" height="440" fill="none" stroke="#ffffff" stroke-opacity="0.1" stroke-width="2" rx="24" />
      <polygon points="800,240 860,340 740,340" fill="none" stroke="url(#gold)" stroke-width="8" stroke-linejoin="round" />
    </svg>`;
    // No text baked into this one: the agency's own name/tagline is already
    // rendered as real HTML directly on top of this background, so a second,
    // unrelated placeholder name (this used to hardcode "Apex Realty Group")
    // would either duplicate it or, worse, show a different agency's name.
  }

  // Architectural Property Graphic
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800" width="1200" height="800">
    <defs>
      <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="${theme.gradientTop}" />
        <stop offset="100%" stop-color="${theme.gradientBottom}" />
      </linearGradient>
      <linearGradient id="accentGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#ffffff" stop-opacity="0.9" />
        <stop offset="100%" stop-color="${theme.accent}" />
      </linearGradient>
      <linearGradient id="overlayGrad" x1="0%" y1="100%" x2="0%" y2="0%">
        <stop offset="0%" stop-color="#000000" stop-opacity="0.85" />
        <stop offset="40%" stop-color="#000000" stop-opacity="0.3" />
        <stop offset="100%" stop-color="#000000" stop-opacity="0.1" />
      </linearGradient>
    </defs>
    
    <rect width="1200" height="800" fill="url(#bg)" />
    
    <g opacity="0.4">
      <rect x="120" y="240" width="280" height="420" fill="#ffffff" fill-opacity="0.04" rx="12" />
      <rect x="150" y="270" width="60" height="80" fill="${theme.accent}" fill-opacity="0.15" rx="4" />
      <rect x="230" y="270" width="60" height="80" fill="${theme.accent}" fill-opacity="0.25" rx="4" />
      <rect x="310" y="270" width="60" height="80" fill="${theme.accent}" fill-opacity="0.15" rx="4" />
      <rect x="150" y="380" width="60" height="80" fill="${theme.accent}" fill-opacity="0.2" rx="4" />
      <rect x="230" y="380" width="60" height="80" fill="${theme.accent}" fill-opacity="0.1" rx="4" />
      <rect x="310" y="380" width="60" height="80" fill="${theme.accent}" fill-opacity="0.3" rx="4" />
      
      <polygon points="450,180 820,180 820,660 450,660" fill="#ffffff" fill-opacity="0.06" />
      <polygon points="420,180 635,80 850,180" fill="url(#accentGrad)" fill-opacity="0.3" />
      
      <rect x="490" y="220" width="130" height="180" fill="#ffffff" fill-opacity="0.12" rx="6" />
      <rect x="650" y="220" width="130" height="180" fill="#ffffff" fill-opacity="0.08" rx="6" />
      <rect x="490" y="430" width="290" height="190" fill="#ffffff" fill-opacity="0.15" rx="8" />
      
      <line x1="400" y1="420" x2="870" y2="420" stroke="${theme.accent}" stroke-width="4" stroke-opacity="0.5" />
      
      <rect x="860" y="290" width="220" height="370" fill="#ffffff" fill-opacity="0.03" rx="8" />
      <rect x="890" y="330" width="70" height="90" fill="${theme.accent}" fill-opacity="0.2" rx="4" />
      <rect x="980" y="330" width="70" height="90" fill="${theme.accent}" fill-opacity="0.1" rx="4" />
    </g>
    
    <circle cx="635" cy="300" r="280" fill="${theme.accent}" fill-opacity="0.08" filter="blur(40px)" />
  </svg>`;
}

export function getPresetSvgDataUri(key: string): string {
  const svg = buildSvgVectorString(key);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export const FALLBACK_SVG_DATA_URI = getPresetSvgDataUri('prop-asset-1');

// Widths we keep on disk. A card on a phone is about 350 css pixels wide, so
// sending it the 1600px master wastes most of the download.
export const IMAGE_WIDTHS = [400, 800] as const;

/**
 * srcset for an uploaded photo, so the browser picks the size it needs. Returns
 * an empty string for placeholders and data URIs, which have no variants.
 */
export function getImageSrcSet(src: string | undefined | null): string {
  const master = getOptimizedImageUrl(src);
  if (!master.startsWith('/uploads/')) return '';
  const hash = master.replace('/uploads/', '').replace(/\.webp$/, '');
  if (!/^[A-Za-z0-9_-]+$/.test(hash)) return '';
  return [
    ...IMAGE_WIDTHS.map((w) => `/uploads/${hash}-${w}.webp ${w}w`),
    `${master} 1600w`,
  ].join(', ');
}

export function getOptimizedImageUrl(src: string | undefined | null, format: 'webp' | 'avif' = 'webp'): string {
  if (!src || typeof src !== 'string' || src.trim() === '') {
    return getPresetSvgDataUri('prop-asset-1');
  }

  if (src.startsWith('data:') || src.startsWith('http://') || src.startsWith('https://')) {
    return src;
  }

  const clean = src
    .replace(/^\/?uploads\//, '')
    .replace(/^\/api\/upload\?hash=/, '')
    .replace(/&format=(webp|avif)/, '')
    .replace(/\.(webp|avif|png|jpg|jpeg|svg)$/i, '')
    .trim();

  if (!clean) {
    return getPresetSvgDataUri('prop-asset-1');
  }

  // If this is a preset asset or known template, return direct vector Data URI for instantaneous, foolproof rendering
  if (
    PRESET_DEFINITIONS[clean] || 
    clean.startsWith('prop-asset-') || 
    clean.startsWith('agency-') || 
    clean.includes('fallback') || 
    clean.includes('default') || 
    clean.includes('residence') ||
    clean.includes('specialist')
  ) {
    return getPresetSvgDataUri(clean);
  }

  return `/uploads/${clean}.${format}`;
}
